import { useState } from "react";
import type { FormEvent } from "react";
import type { EnrichedBookmark, EnrichedTag } from "../../shared/types.ts";
import { normalizeUrlForMatching } from "../../shared/url-utils.ts";
import { useApp } from "../context/AppContext.tsx";
import { apiPost } from "../utils/api.ts";
import { DuplicateWarning } from "./DuplicateWarning.tsx";
import { TagInput } from "./TagInput.tsx";
import { DomainTagSuggestions } from "./DomainTagSuggestions.tsx";
import { Button } from "./Button.tsx";

interface AddBookmarkProps {
  onClose: () => void;
  onBookmarkAdded: (bookmark: EnrichedBookmark) => void;
  availableTags: EnrichedTag[];
  onTagsChanged?: () => void;
}

export function AddBookmark({
  onClose,
  onBookmarkAdded,
  availableTags,
  onTagsChanged,
}: AddBookmarkProps) {
  const { bookmarks } = useApp();
  const [url, setUrl] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [duplicates, setDuplicates] = useState<EnrichedBookmark[] | null>(null);

  function findDuplicates(inputUrl: string): EnrichedBookmark[] {
    const inputBase = normalizeUrlForMatching(inputUrl);
    if (!inputBase) return [];
    return bookmarks.filter((b) =>
      normalizeUrlForMatching(b.subject) === inputBase
    );
  }

  async function saveBookmark() {
    setLoading(true);
    setError(null);

    try {
      const response = await apiPost("/api/bookmarks", {
        url: url.trim(),
        tags,
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to add bookmark");
      }

      const data = await response.json();
      if (tags.length > 0 && onTagsChanged) {
        onTagsChanged();
      }
      onBookmarkAdded(data.bookmark);
    } catch (err: any) {
      setError(err.message);
      setLoading(false);
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!url.trim()) return;

    const matches = findDuplicates(url.trim());
    if (matches.length > 0) {
      setDuplicates(matches);
      return;
    }

    await saveBookmark();
  }

  function handleCancelDuplicate() {
    setDuplicates(null);
  }

  async function handleSaveAnyway() {
    await saveBookmark();
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full p-6 fade-in">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-gray-800">Add Bookmark</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
            disabled={loading}
          >
            ×
          </button>
        </div>

        {duplicates
          ? (
            <DuplicateWarning
              duplicates={duplicates}
              onCancel={handleCancelDuplicate}
              onContinue={handleSaveAnyway}
              loading={loading}
            />
          )
          : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label
                  htmlFor="url"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  URL
                </label>
                <input
                  type="url"
                  id="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://example.com"
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-coral focus:border-transparent outline-none transition"
                  disabled={loading}
                  autoFocus
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tags (optional)
                </label>
                <TagInput
                  tags={tags}
                  onTagsChange={setTags}
                  availableTags={availableTags}
                  disabled={loading}
                  compact
                />
                <DomainTagSuggestions
                  url={url}
                  selected={tags}
                  available={availableTags}
                  onChange={setTags}
                  disabled={loading}
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={onClose}
                  disabled={loading}
                  fullWidth
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  loading={loading}
                  disabled={!url.trim()}
                  fullWidth
                >
                  {loading ? "Adding..." : "Add Bookmark"}
                </Button>
              </div>
            </form>
          )}

        <p className="text-xs text-gray-500 mt-4 text-center">
          The page title will be automatically fetched and saved with your
          bookmark
        </p>
      </div>
    </div>
  );
}
